import {FC, useCallback, useState} from 'react';
import style from './style.module.scss';
import Link from 'components/Link';
import {Typography} from 'components/Typography';
import classNames from 'classnames';
import {Tooltip} from 'components/Tooltip';
import {useProductRoutes} from 'core/modules/routing/hooks/useProductRoute';
import {VINTAGE_ITEMS_COUNT} from 'modules/products/constants';
import {formatPrice} from 'core/helpers';
import {PRICE_STYLE} from 'core/constants';
import {useIsSkeleton} from 'modules/skeleton/hooks/useIsSkeleton';
import Icon from 'components/Icon';
import {IVintage} from 'modules/products/types';
import {useIsPrive} from 'core/hooks/useIsPrive';
import {withRedirectParam} from 'modules/product-card/helpers/redirect';

interface IProps {
    items?: IVintage[];
    parentCode?: string;
}

export const ProductVintage: FC<IProps> = ({items = [], parentCode}) => {
    const {
        product: {url},
    } = useProductRoutes();

    const isSkeleton = useIsSkeleton();
    const isPrive = useIsPrive();

    const [isShowMore, setIsShowMore] = useState(false);

    const getAvailabilityBlock = useCallback((type?: string) => {
        switch (type) {
            case 'IN_STOCK':
                return (
                    <Typography
                        className={classNames(style.availability, style.full)}
                        color={'gray60'}
                        variant={'label-14-r'}
                    >
                        в наличии
                    </Typography>
                );
            case 'WINERY_ONLY':
                return (
                    <Typography
                        className={classNames(style.availability, style.part)}
                        color={'gray60'}
                        variant={'label-14-r'}
                    >
                        только в винотеке
                    </Typography>
                );
            case 'OUT_OF_STOCK':
            default:
                return (
                    <Typography
                        className={classNames(style.availability, style.outOfStock)}
                        color={'gray60'}
                        variant={'label-14-r'}
                    >
                        нет в наличии
                    </Typography>
                );
        }
    }, []);

    const initialItems = items.slice(0, VINTAGE_ITEMS_COUNT);

    const otherItems = items.slice(VINTAGE_ITEMS_COUNT);

    const itemsCount = items?.length || 0;

    const shouldShowBasePrice = ({stockType, basePrice}: {stockType: IVintage['stockType']; basePrice: number}) => {
        if (!basePrice) {
            return false;
        }

        return 'OUT_OF_STOCK' === stockType || ('WINERY_ONLY' === stockType && isPrive);
    };

    const handleShowMoreClick = () => setIsShowMore(!isShowMore);

    return (
        <div className={style.vintage}>
            <Typography className={style.header} color={'gray60'}>
                <span data-skeleton-item={true}>
                    Винтаж
                    <span className={style.headerColon}>:</span>
                </span>
                {!isSkeleton && (
                    <Tooltip
                        color={'red'}
                        place={'top-start'}
                        text={'Год урожая винограда, из которого произведено вино.'}
                    >
                        <Icon icon={'question-sm'} className={style.icon} />
                    </Tooltip>
                )}
            </Typography>
            <div className={style.desktop}>
                <div className={style.itemsWrapper}>
                    {[...initialItems, ...(isShowMore ? otherItems : [])]?.map(
                        ({code, year, price, basePrice, stockType}) => {
                            return (
                                <div data-skeleton-item={true} key={code}>
                                    <Link
                                        className={classNames({[style.active]: parentCode === code}, style.vintageItem)}
                                        disabled={parentCode === code}
                                        href={withRedirectParam(url({id: code}))}
                                        shallow={false}
                                        key={code}
                                    >
                                        <Typography className={style.text}>
                                            <Typography variant={'label-14-r-fluid'} color={'black100'}>
                                                {year} г.
                                            </Typography>
                                            <Typography variant={'label-14-r'} color={'black100'}>
                                                {' '}
                                                —{' '}
                                                {formatPrice(
                                                    shouldShowBasePrice({stockType, basePrice}) ? basePrice : price,
                                                    {
                                                        style: PRICE_STYLE.currency,
                                                    },
                                                )}
                                            </Typography>
                                        </Typography>
                                        {getAvailabilityBlock(stockType)}
                                    </Link>
                                </div>
                            );
                        },
                    )}
                </div>
                {VINTAGE_ITEMS_COUNT < itemsCount && (
                    <Typography
                        className={style.showMore}
                        color={'gray60'}
                        element={'div'}
                        onClick={handleShowMoreClick}
                    >
                        {isShowMore ? 'Свернуть' : 'Еще винтажи'}
                    </Typography>
                )}
            </div>
            <div className={style.tablet}>
                <div className={style.itemsWrapper}>
                    {items?.map(({code, year, price, stockType, basePrice}) => {
                        return (
                            <Link
                                className={classNames({[style.active]: parentCode === code}, style.vintageItem)}
                                disabled={parentCode === code}
                                href={withRedirectParam(url({id: code}))}
                                key={code}
                                data-skeleton-item={true}
                            >
                                <Typography variant={'label-14-r'} color={'black100'}>
                                    <Typography variant={'label-14-r-fluid'}>{year} г.</Typography>
                                    <Typography variant={'label-14-r'}>
                                        {' '}
                                        —{' '}
                                        {formatPrice(shouldShowBasePrice({stockType, basePrice}) ? basePrice : price, {
                                            style: PRICE_STYLE.currency,
                                        })}
                                    </Typography>
                                </Typography>
                                {getAvailabilityBlock(stockType)}
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
